import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import KioskModal from "./KioskModal";

// Переводы намеренно локальные, а не через i18n: модалка показывается независимо
// от того, какой язык выбрал пользователь, и сама прокручивает все три.
const translations = {
  ru: { offline_title: "Нет соединения", offline_message: "Подойдите позже" },
  en: { offline_title: "No Connection", offline_message: "Please try again later" },
  kk: { offline_title: "Интернет жоқ", offline_message: "Кейінірек қайта көріңіз" },
};

function NetworkStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState("ru");
  const languages = ["ru", "en", "kk"];

  const intervalRef = useRef(null);
  const languageIntervalRef = useRef(null);
  const cancelledRef = useRef(false);

  // Проверка соединения
  const checkConnection = async () => {
    if (cancelledRef.current) return;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/api/v2/check/ping`, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
        keepalive: true,
      });

      clearTimeout(timeoutId);
      if (!cancelledRef.current) {
        setIsOnline(response.ok);
      }
    } catch (err) {
      if (!cancelledRef.current) {
        setIsOnline(false);
      }
    }
  };

  // Основной useEffect: проверка интернета + Electron + системные события
  useEffect(() => {
    cancelledRef.current = false;

    checkConnection();

    intervalRef.current = setInterval(checkConnection, 5000);

    const handleOnline = () => {
      setIsOnline(true);
      checkConnection();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Поддержка Electron (если есть)
    if (window.electron?.ipcRenderer) {
      const handleElectronStatus = (event, data) => {
        setIsOnline(data.online);
      };
      window.electron.ipcRenderer.on('connection-status', handleElectronStatus);

      return () => {
        window.electron.ipcRenderer.removeAllListeners('connection-status');
      };
    }

    return () => {
      cancelledRef.current = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (languageIntervalRef.current) clearInterval(languageIntervalRef.current);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Переключение языка — только если модалка видна
  useEffect(() => {
    if (!isOnline) {
      languageIntervalRef.current = setInterval(() => {
        setCurrentLanguage(prev => {
          const i = languages.indexOf(prev);
          return languages[(i + 1) % languages.length];
        });
      }, 4000);
    }

    return () => {
      if (languageIntervalRef.current) clearInterval(languageIntervalRef.current);
    };
  }, [isOnline]);

  return (
    <AnimatePresence>
      {!isOnline && (
        <KioskModal
          tone="danger"
          // системные модалки должны перекрывать модалки экранов (те на z-50)
          zClass="z-[60]"
          title={translations[currentLanguage].offline_title}
          description={translations[currentLanguage].offline_message}
          titleKey={currentLanguage}
          descriptionKey={currentLanguage}
          icon={<WifiAnimation />}
        />
      )}
    </AnimatePresence>
  );
}

// Пульсирующие дуги Wi-Fi — цвет наследуется от тона модалки (currentColor)
const WifiAnimation = React.memo(() => (
  <svg
    className="w-[5.6vh] h-[5.6vh]"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path fillOpacity="0.35" d="M9.9,5C6.8,5,4,6.4,2.2,8.7l1.1,1.1c1.6-2,4-3.2,6.7-3.2c2.7,0,5.1,1.3,6.7,3.2l1.1-1.1C15.8,6.4,13,5,9.9,5z">
      <animate attributeName="fill-opacity" values="0.35;1;0.35" dur="1.4s" repeatCount="indefinite" />
    </path>
    <path fillOpacity="0.35" d="M9.9,8c-2.3,0-4.3,1.1-5.6,2.8l1.1,1.1c1-1.4,2.6-2.4,4.5-2.4c1.9,0,3.5,0.9,4.5,2.4l1.1-1.1C14.2,9.1,12.2,8,9.9,8z">
      <animate attributeName="fill-opacity" values="0.35;1;0.35" dur="1.4s" begin="0.15s" repeatCount="indefinite" />
    </path>
    <path fillOpacity="0.35" d="M9.9,11c-1.5,0-2.7,0.8-3.4,2l1.1,1.1c0.4-0.9,1.3-1.6,2.3-1.6s2,0.7,2.3,1.6l1.1-1.1C12.6,11.8,11.4,11,9.9,11z">
      <animate attributeName="fill-opacity" values="0.35;1;0.35" dur="1.4s" begin="0.3s" repeatCount="indefinite" />
    </path>
    <circle fillOpacity="0.35" cx="9.9" cy="15.3" r="1">
      <animate attributeName="fill-opacity" values="0.35;1;0.35" dur="1.4s" begin="0.45s" repeatCount="indefinite" />
    </circle>
  </svg>
));

export default NetworkStatus;
